import { motion } from "framer-motion";
import { Sparkles, FileText, BookCopy } from "lucide-react";
import { ParticleOverlay } from "./ParticleOverlay";
import { BlurIn } from "./ScrollAnimations";

const customisations = [
  { icon: Sparkles, title: "Gold Foiling", subtitle: "Company Logo & Personalised Name", description: "Elevate your corporate diaries with exquisite gold foil stamping. Add your company logo on the cover and personalised names for each recipient.", image: "/gold_foiling.png" },
  { icon: FileText, title: "Page Insertions", subtitle: "Custom Pages Inside Your Diary", description: "Insert custom pages with your company's mission, calendar, contacts, or branding materials.", image: "/page_insertion.png" },
  { icon: BookCopy, title: "Fully Customised Diaries", subtitle: "For Bulk Orders of 2000 Nos.+", description: "Go beyond standard options — design your diary from scratch. Choose cover material, paper quality, layout, binding style, and more.", image: "/fully_customised.png" },
];

const cardVariants = {
  hidden: { opacity: 0, y: 60, rotateX: 8 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: {
      duration: 0.8,
      delay: i * 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export const CustomisationSection = () => {
  return (
    <section id="customisation" data-testid="customisation-section" className="py-24 sm:py-32 bg-[#FAFAF7] relative overflow-hidden">
      <ParticleOverlay color="#C8A23A" />
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl relative z-10">
        <BlurIn className="text-center max-w-2xl mx-auto mb-20">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C8A23A] mb-4 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Customisation</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1A1A1A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="customisation-heading">
            Three Ways to Make It <span className="text-[#7A1A31]">Yours</span>
          </h2>
          <p className="text-base text-[#6B7280] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>We offer three tiers of customisation for bulk orders.</p>
        </BlurIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" style={{ perspective: "1200px" }}>
          {customisations.map((item, i) => (
            <motion.div
              key={item.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={cardVariants}
              whileHover={{ y: -12, boxShadow: "0 25px 50px rgba(0,0,0,0.08)" }}
              transition={{ duration: 0.4 }}
              className="group bg-white border border-black/5 rounded-xl overflow-hidden transition-shadow duration-500"
              data-testid={`customisation-card-${i}`}
            >
              <div className="relative h-56 overflow-hidden">
                <motion.img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                <motion.div
                  className="absolute top-5 left-5 w-10 h-10 bg-[#C8A23A] rounded-full flex items-center justify-center shadow-lg"
                  whileHover={{ rotate: 360, scale: 1.15 }}
                  transition={{ duration: 0.6 }}
                >
                  <item.icon className="w-5 h-5 text-white" />
                </motion.div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-xl text-[#1A1A1A] mb-1.5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{item.title}</h3>
                <span className="text-xs tracking-[0.1em] uppercase text-[#C8A23A] font-medium block mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.subtitle}</span>
                <p className="text-sm text-[#6B7280] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
